/**
 * Unified toast wrapper for application-wide notifications.
 * Supports custom styling and configurable position.
 *
 * Usage:
 * ```
 * import { toast } from '@/lib/toast'
 *
 * // Default (top-right)
 * toast.success('Password reset email sent')
 *
 * // With symbol badge
 * toast.success('Order submitted', { symbol: 'BTC/USD' })
 *
 * // With custom position (for order-form)
 * toast.success('Order filled', { position: 'bottom-right', symbol: 'BTC/USD' })
 * ```
 */
import { toast as sonnerToast } from 'sonner'

import { NotificationToastCard } from '@/components/ui/notification-toast'
import type { NotificationSeverity } from '@/service/ws/classify-notification'

type ToastPosition =
  | 'top-right'
  | 'top-left'
  | 'top-center'
  | 'bottom-right'
  | 'bottom-left'
  | 'bottom-center'

type ToastOptions = {
  symbol?: string
  position?: ToastPosition
  duration?: number
  /** Showing a toast with the same id replaces it instead of stacking a new one. */
  id?: string
  /** A button under the message; clicking it runs `onClick` and dismisses the toast. */
  action?: { label: string; onClick: () => void }
}

function showToast(severity: NotificationSeverity, message: string, options?: ToastOptions) {
  const { symbol, position = 'top-right', action, id } = options || {}
  // A toast with a button stays up longer, so there's time to reach it.
  const duration = options?.duration ?? (action ? 6000 : 3000)

  sonnerToast.custom(
    (toastId) => (
      <NotificationToastCard
        severity={severity}
        title={message}
        symbol={symbol}
        size="compact"
        action={
          action && {
            label: action.label,
            onClick: () => {
              sonnerToast.dismiss(toastId)
              action.onClick()
            },
          }
        }
      />
    ),
    { duration, position, id }
  )
}

export const toast = {
  success: (message: string, options?: ToastOptions) => showToast('success', message, options),
  error: (message: string, options?: ToastOptions) => showToast('error', message, options),
  warning: (message: string, options?: ToastOptions) => showToast('warning', message, options),
  info: (message: string, options?: ToastOptions) => showToast('info', message, options),
}
